import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, CheckCheck, Loader2 } from 'lucide-react';
import notificationsService, {
  type NotificationRecord,
} from '../../services/notifications.service';
import { isNotificationsUiAllowed } from '../../config/featureFlags';
import { getApiErrorMessage } from '../../utils/apiError';
import { Button } from '../ui/Button';
import { shell } from '../../styles/pageShell';

type NotificationBellProps = {
  pollMs?: number;
};

export const NotificationBell: React.FC<NotificationBellProps> = ({ pollMs = 45_000 }) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [items, setItems] = useState<NotificationRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!isNotificationsUiAllowed()) return;
    try {
      const [count, list] = await Promise.all([
        notificationsService.unreadCount(),
        open
          ? notificationsService.list({ page: 1, pageSize: 8 })
          : Promise.resolve(null),
      ]);
      setUnread(count);
      if (list) setItems(list.data);
    } catch {
      // ignore poll errors
    }
  }, [open]);

  useEffect(() => {
    void refresh();
    const id = window.setInterval(() => void refresh(), pollMs);
    return () => window.clearInterval(id);
  }, [refresh, pollMs]);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setError(null);
    notificationsService
      .list({ page: 1, pageSize: 8 })
      .then((res) => {
        setItems(res.data);
        setUnread(res.unreadCount);
      })
      .catch((err) => setError(getApiErrorMessage(err, 'Failed to load notifications')))
      .finally(() => setLoading(false));
  }, [open]);

  if (!isNotificationsUiAllowed()) return null;

  const onOpenItem = async (n: NotificationRecord) => {
    if (!n.isRead) {
      try {
        await notificationsService.markRead(n.id);
        setUnread((c) => Math.max(0, c - 1));
        setItems((prev) => prev.map((x) => (x.id === n.id ? { ...x, isRead: true } : x)));
      } catch {
        // continue navigation
      }
    }
    setOpen(false);
    if (n.linkPath) navigate(n.linkPath);
    else navigate('/notifications');
  };

  const onMarkAll = async () => {
    try {
      await notificationsService.markAllRead();
      setUnread(0);
      setItems((prev) => prev.map((x) => ({ ...x, isRead: true })));
    } catch (err) {
      setError(getApiErrorMessage(err, 'Could not mark all read'));
    }
  };

  return (
    <div className="relative">
      <button
        type="button"
        className="relative inline-flex h-10 w-10 items-center justify-center rounded-full border border-[#E0DCE5] bg-white text-[#261D30] hover:bg-neutral-50"
        aria-label={`Notifications${unread ? ` (${unread} unread)` : ''}`}
        onClick={() => setOpen((v) => !v)}
      >
        <Bell className="h-4 w-4" />
        {unread > 0 && (
          <span className="absolute -right-1 -top-1 inline-flex min-w-[1.1rem] items-center justify-center rounded-full bg-[#602673] px-1 text-[10px] font-bold text-white">
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {open && (
        <>
          <button
            type="button"
            className="fixed inset-0 z-40 cursor-default bg-transparent"
            aria-label="Close notifications"
            onClick={() => setOpen(false)}
          />
          <div className="absolute right-0 z-50 mt-2 w-[min(100vw-1.5rem,22rem)] overflow-hidden rounded-xl border border-[#E0DCE5] bg-white shadow-lg">
            <div className="flex items-center justify-between border-b border-[#E0DCE5] px-3 py-2">
              <p className="text-sm font-semibold text-[#261D30]">Notifications</p>
              <Button variant="ghost" size="sm" onClick={() => void onMarkAll()} disabled={!unread}>
                <CheckCheck className="mr-1 h-3.5 w-3.5" />
                Mark all
              </Button>
            </div>
            <div className="max-h-80 overflow-y-auto">
              {loading && (
                <p className={`px-3 py-6 text-center text-sm ${shell.textMuted}`}>
                  <Loader2 className="mr-1 inline h-4 w-4 animate-spin" /> Loading…
                </p>
              )}
              {error && <p className="px-3 py-2 text-xs text-red-700">{error}</p>}
              {!loading && items.length === 0 && (
                <p className={`px-3 py-8 text-center text-sm ${shell.textMuted}`}>
                  You&apos;re all caught up.
                </p>
              )}
              {!loading &&
                items.map((n) => (
                  <button
                    key={n.id}
                    type="button"
                    className={`block w-full border-b border-[#F0ECF3] px-3 py-2.5 text-left hover:bg-neutral-50 ${
                      n.isRead ? 'opacity-70' : 'bg-[#Faf8fc]'
                    }`}
                    onClick={() => void onOpenItem(n)}
                  >
                    <p className="text-sm font-medium text-[#261D30]">{n.title}</p>
                    <p className={`mt-0.5 line-clamp-2 text-xs ${shell.textMuted}`}>{n.message}</p>
                    <p className={`mt-1 text-[11px] ${shell.textMuted}`}>
                      {new Date(n.createdAt).toLocaleString()}
                    </p>
                  </button>
                ))}
            </div>
            <div className="border-t border-[#E0DCE5] px-3 py-2">
              <Button
                variant="outline"
                size="sm"
                className="w-full"
                onClick={() => {
                  setOpen(false);
                  navigate('/notifications');
                }}
              >
                View all
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default NotificationBell;
